import React, { useCallback, useEffect, useMemo, useState } from "react";
import BackButton from "../../generic/elements/backbutton/BackButton";
import { Input, Select, Space, Table } from "antd";
import "./reportinglogs.css";
import { useGetOrgIdentifier } from "../../generic/hooks/useGetOrgIdentifier";
import { performPostRequest } from "../../../service/network/NetworkService";
import { getApiUrl } from "../../../service/network/UrlMapper";
import IntentType from "../../../intentexecutor/utils/IntentType";
import ErrorNotificationComponent from "../../../components/generic/error/ErrorNotificationComponent";
import EditModal, { reportLogDataUpdateResponse } from "./EditModal";
import EditButton from "./EditButton";
import { useObservable } from "../../generic/hooks/useObservable";
import PrimaryButton from "../../../components/generic/buttons/PrimaryButton";
import SearchAndSelect from "../../../components/generic/search/SearchAndSelect";
import { MultiSelect } from "../../generic/elements/multiselect/MultiSelect";
import { useReadMemory } from "../../generic/hooks/useReadMemory";
import MemoryType from "../../../service/memory/enum/MemoryType";
import PatientReporting from "./PatientReporting";
import EleventhHourReporting from "./EleventhHourReporting";
import ReportingLogCellModal from "./ReportingLogCellModal";
import {
  CONFIG_KEYS,
  EXPANDED_ROWS_INITIAL,
  REPORTING_YEARS,
  ELEVENTH_HOUR_REPORT_OPTION,
  TOGGLE_TABLE_OPTIONS,
} from "./constants";
import { exportReportingToExcel } from "./exportReportingToExcel";
import { getCurrentTimeZone } from "../../../utils/dateutils";
import { getTableConfigs } from "./getTableConfigs";
import { useCellDetailModal } from "./useCellDetailModal";
import { useRecoilValue } from "recoil";
import { officeLabel } from "../../../components/recoil/officeLabelRecoil";
import { currentConfiguration } from "../../../service/memory/recoil/configurationstore";
import ConfigurationConstants from "../../utils/configurationconstants";

const REPORTING_CONFIG_KEYS = [
  CONFIG_KEYS.DASHBOARD_DATA,
  CONFIG_KEYS.MONTHS,
  CONFIG_KEYS.QUARTER,
  CONFIG_KEYS.MAPPER,
];

const ReportingLogs = () => {
  const userDetails = useReadMemory(MemoryType.USER);
  const organizationIdentifier = useGetOrgIdentifier();
  const configuration = useRecoilValue(currentConfiguration);
  const [showTeamsSelect, setShowTeamsSelect] = useState(false);

  const teamsOptions = useMemo(() => {
    return showTeamsSelect
      ? userDetails?.teams
          ?.filter(
            (team) =>
              team?.organizationIdentifier ===
              userDetails?.organizationIdentifier,
          )
          ?.map((team) => ({
            ...team,
            label: team?.name,
            value: team?.id,
          }))
      : [];
  }, [
    showTeamsSelect,
    userDetails?.organizationIdentifier,
    userDetails?.teams,
  ]);

  const [teams, setTeams] = useState([]);
  const [serviceHour, setServiceHour] = useState("");
  const [serviceRequest, setServiceRequest] = useState("");
  const [reportingData, setReportingData] = useState({});
  const [patientReportingData, setPatientReportingData] = useState([]);
  const [eleventhHourExport, setEleventhHourExport] = useState(null);
  const [year, setYear] = useState(REPORTING_YEARS[0].value);
  const [loading, setLoading] = useState(false);
  const [downloadLoading, setDownloadLoading] = useState(false);
  const [reload, setReload] = useState(false);
  const [wagePerHour, setWagePerHour] = useState(-1);
  const [mileage, setMileage] = useState(-1);
  const [months, setMonths] = useState([]);
  const [quarters, setQuarters] = useState([]);
  const [mapper, setMapper] = useState({});
  const label = useRecoilValue(officeLabel);

  useEffect(() => {
    if (!showTeamsSelect) {
      setTeams([]);
      return;
    }
    const mapped = (teamsOptions ?? []).map((team) => ({
      ...team,
      name: team.label,
      id: team.value,
    }));
    if (!mapped.length) {
      setTeams([]);
      return;
    }
    setTeams((prev) => {
      if (!prev?.length) return mapped;
      const validIds = new Set(mapped.map((t) => String(t.value)));
      const stillValid = prev.filter((t) => validIds.has(String(t.value)));
      return stillValid.length === prev.length ? prev : mapped;
    });
  }, [showTeamsSelect, teamsOptions]);

  const [
    typesOfToggleTableFiltersSelected,
    setTypesOfToggleTableFiltersSelected,
  ] = useState(TOGGLE_TABLE_OPTIONS);
  const [expandedRows, setExpandedRows] = useState(EXPANDED_ROWS_INITIAL);

  useObservable(reportLogDataUpdateResponse, setReload);

  const {
    cellModalOpen,
    setCellModalOpen,
    cellModalLoading,
    cellModalData,
    cellModalTableKey,
    cellModalCategoryKey,
    cellModalMonthNumber,
    cellModalQuarterNumber,
    getColumnsWithCellClick,
  } = useCellDetailModal(organizationIdentifier, teams, year, months, quarters);

  const exportContext = useMemo(() => {
    const monthLabel =
      cellModalMonthNumber != null && months?.length
        ? months[cellModalMonthNumber - 1]
        : "";
    const quarterLabel =
      cellModalQuarterNumber != null && quarters?.length
        ? quarters[cellModalQuarterNumber - 1]
        : "";
    const categoryName =
      mapper[cellModalCategoryKey] ?? cellModalCategoryKey ?? "";
    const tableName =
      TOGGLE_TABLE_OPTIONS.find((o) => o.value === cellModalTableKey)?.label ??
      cellModalTableKey ??
      "";
    return {
      year,
      monthLabel,
      quarterLabel,
      categoryName,
      tableName,
    };
  }, [
    year,
    months,
    quarters,
    mapper,
    cellModalTableKey,
    cellModalCategoryKey,
    cellModalMonthNumber,
    cellModalQuarterNumber,
  ]);

  const handleExpand = (type) => (expanded, record) => {
    setExpandedRows((prev) => ({
      ...prev,
      [type]: expanded ? record.key : null,
    }));
  };

  const handleYear = (data) => setYear(data);

  const handleSelect = (selectedValues) => {
    const finalSelection = !selectedValues?.length
      ? teamsOptions
      : selectedValues;
    const transformedTeams = finalSelection.map((team) => ({
      ...team,
      name: team.label,
      id: team.value,
    }));
    const isSame =
      transformedTeams.length === teams.length &&
      transformedTeams.every((team, i) => team.id === teams[i]?.id);
    if (!isSame) setTeams(transformedTeams);
  };

  const handleEleventhHourExportChange = useCallback((data) => {
    setEleventhHourExport(data);
  }, []);

  const handleDownload = async () => {
    setDownloadLoading(true);
    try {
      await exportReportingToExcel({
        reportingData,
        patientReportingData,
        months,
        quarters,
        mapper,
        serviceHour,
        serviceRequest,
        eleventhHourReport: eleventhHourExport,
      });
    } catch (error) {
      ErrorNotificationComponent(error);
    } finally {
      setDownloadLoading(false);
    }
  };

  useEffect(() => {
    const fetchConfig = async () => {
      const payload = {
        intent: "get_configuration",
        payload: { configurationKey: REPORTING_CONFIG_KEYS },
        context: { organizationIdentifier },
      };
      try {
        const result = await performPostRequest(
          getApiUrl()[IntentType.GET_CONFIGURATION],
          JSON.stringify(payload),
        );
        const data = await result.json();
        if (data) {
          const dashboardKeyData = JSON.parse(data[0]?.configurationValue);
          setMonths(JSON.parse(data[1]?.configurationValue));
          setQuarters(JSON.parse(data[2]?.configurationValue));
          setMapper(JSON.parse(data[3]?.configurationValue));
          dashboardKeyData?.forEach((item) => {
            if (item?.key === "WAGE_PER_HOUR") setWagePerHour(item?.value);
            if (item?.key === "MILEAGE") setMileage(item?.value);
          });
        }
      } catch (error) {
        ErrorNotificationComponent(error);
      }
    };
    fetchConfig();
  }, [organizationIdentifier]);

  useEffect(() => {
    if (wagePerHour <= -1 || mileage <= -1) return;

    const fetchReportingLogs = async () => {
      setLoading(true);
      try {
        const result = await performPostRequest(
          getApiUrl()[IntentType.GET_REPORTING_LOGS],
          JSON.stringify({
            organizationIdentifier,
            year,
            filters: typesOfToggleTableFiltersSelected
              ?.map((o) => o.value)
              .filter((value) => value !== ELEVENTH_HOUR_REPORT_OPTION.value),
            rate_per_hour: wagePerHour,
            miles_per_hour: mileage,
            timezone: getCurrentTimeZone(),
            teamIds: teams?.map((t) => t.value),
          }),
        );
        const resultJson = await result.json();
        setReportingData(resultJson);
      } catch (error) {
        ErrorNotificationComponent(error);
      } finally {
        setLoading(false);
        setReload(false);
      }
    };
    fetchReportingLogs();
  }, [
    wagePerHour,
    mileage,
    year,
    reload,
    organizationIdentifier,
    typesOfToggleTableFiltersSelected,
    teams,
  ]);

  const customExpandIcon = ({ onExpand, record }, allowedTypes) => {
    const allowed = Array.isArray(allowedTypes)
      ? allowedTypes
      : allowedTypes
        ? [allowedTypes]
        : [];
    return allowed.includes(record.type) ? (
      <EditButton onExpand={onExpand} record={record} />
    ) : null;
  };

  const tableConfigs = useMemo(
    () => getTableConfigs({ reportingData, months, quarters, mapper }),
    [reportingData, months, quarters, mapper],
  );

  const selectedKeys = useMemo(
    () => typesOfToggleTableFiltersSelected.map((s) => s.value),
    [typesOfToggleTableFiltersSelected],
  );


  const isEleventhHourReportAllowed = useMemo(
    () =>
      Boolean(
        configuration[
          ConfigurationConstants.FEATUREACCESS_ELEVENTHHOUR_REPORT
        ]?.visibility,
      ),
    [configuration],
  );

  const reportOptions = useMemo(
    () =>
      isEleventhHourReportAllowed
        ? [...TOGGLE_TABLE_OPTIONS, ELEVENTH_HOUR_REPORT_OPTION]
        : TOGGLE_TABLE_OPTIONS,
    [isEleventhHourReportAllowed],
  );

  useEffect(() => {
    setTypesOfToggleTableFiltersSelected((prev) => {
      const withoutEleventhHour = prev.filter(
        (option) => option.value !== ELEVENTH_HOUR_REPORT_OPTION.value,
      );
      if (!isEleventhHourReportAllowed) {
        return withoutEleventhHour.length === prev.length
          ? prev
          : withoutEleventhHour;
      }
      if (
        prev.some((option) => option.value === ELEVENTH_HOUR_REPORT_OPTION.value)
      ) {
        return prev;
      }
      return [...prev, ELEVENTH_HOUR_REPORT_OPTION];
    });
  }, [isEleventhHourReportAllowed]);

  useEffect(() => {
    const configurationAllowTeamSelect =
      configuration[ConfigurationConstants.FEATUREACCESS_MENUHEADER_TEAMSELECT];
    setShowTeamsSelect(configurationAllowTeamSelect?.visibility);
  }, [configuration]);

  return (
    <section className="section-content">
      <div className="form-renderer-back-button">
        <BackButton />
        <p>Reporting Logs</p>
      </div>

      <div>
        <Space>
          <div className="event-type-select-wrapper">
            <div className="event-type-label">Reporting Year:</div>
            <Select
              defaultValue={year}
              options={REPORTING_YEARS}
              placeholder="Please select"
              className="reporting-select"
              popupClassName="select-dropdown"
              onChange={handleYear}
            />
          </div>
          {showTeamsSelect && (
            <div className="event-type-select-wrapper">
              <div className="event-type-label">{`Select ${label}:`}</div>
              <MultiSelect
                options={teamsOptions}
                value={teams}
                onChange={handleSelect}
                placeholder={`Click here to select ${label}s`}
                isClearable={false}
                allLabel={`All ${label}s`}
              />
            </div>
          )}
        </Space>
      </div>

      <Space>
        <div className="event-type-select-wrapper">
          <div className="event-type-label">Hours Wage Value:</div>
          <Input
            className="reporting-select"
            value={`$ ${wagePerHour}`}
            readOnly
          />
          <div className="event-type-label">Per hour</div>
        </div>
        <div className="event-type-select-wrapper">
          <div className="event-type-label">Mileage Value:</div>
          <Input className="reporting-select" value={`$ ${mileage}`} readOnly />
          <div className="event-type-label">Per hour</div>
        </div>
      </Space>

      <div className="event-type-select-wrapper">
        <div className="event-type-label">Reports:</div>
        <SearchAndSelect
          mode="multiple"
          selectedValues={typesOfToggleTableFiltersSelected}
          placeholder="Click here to select reports"
          setValue={setTypesOfToggleTableFiltersSelected}
          style={{ width: "100%" }}
          options={reportOptions}
          selectOptionType="Simple"
          noDebounceSearch={true}
        />
      </div>

      <div>
        <PrimaryButton
          buttonText="Export"
          handleOnClick={handleDownload}
          loading={downloadLoading}
        />
      </div>

      <div className="reporting-tables">
        {tableConfigs
          .filter((cfg) => selectedKeys.includes(cfg.key))
          .map((cfg) => (
            <div key={cfg.key}>
              <h5 className="table-title">{cfg.title}</h5>
              <h6 className="table-sub-title">{cfg.subTitle}</h6>
              {cfg.key === "patientReporting" ? (
                <PatientReporting
                  patientReportingData={patientReportingData}
                  setPatientReportingData={setPatientReportingData}
                  teams={teams}
                  setLoading={setLoading}
                  loading={loading}
                  months={months}
                  wagePerHour={wagePerHour}
                  mileage={mileage}
                  year={year}
                  expandedRows={expandedRows}
                  setExpandedRows={setExpandedRows}
                  customExpandIcon={customExpandIcon}
                  handleExpand={handleExpand}
                  quarters={quarters}
                  mapper={mapper}
                  setServiceHour={setServiceHour}
                  setServiceRequest={setServiceRequest}
                  getColumnsWithCellClick={getColumnsWithCellClick}
                />
              ) : (
                <Table
                  loading={loading}
                  className="tableStyling"
                  pagination={false}
                  dataSource={cfg.dataSource}
                  columns={getColumnsWithCellClick(cfg.columns, cfg.key)}
                  expandable={
                    ( !showTeamsSelect || teams.length === 1) && cfg.expandable?.enabled
                      ? {
                          expandedRowRender: (record) => (
                            <EditModal
                              months={months}
                              keyName={
                                typeof cfg.expandable.keyName === "function"
                                  ? cfg.expandable.keyName(record)
                                  : cfg.expandable.keyName
                              }
                              year={year}
                              onCancel={() =>
                                setExpandedRows((prev) => ({
                                  ...prev,
                                  [cfg.expandable.expandKey]: null,
                                }))
                              }
                              teamId={teams.length === 1 ? teams[0]?.id : ""}
                            />
                          ),
                          expandIcon: (props) =>
                            customExpandIcon(
                              props,
                              cfg.expandable.expandTitles ??
                                cfg.expandable.expandTitle,
                            ),
                          expandIconColumnIndex: cfg.columns.length,
                          onExpand: handleExpand(cfg.expandable.expandKey),
                          expandedRowKeys: [
                            expandedRows[cfg.expandable.expandKey],
                          ],
                        }
                      : undefined
                  }
                />
              )}
            </div>
          ))}
        {isEleventhHourReportAllowed &&
          selectedKeys.includes(ELEVENTH_HOUR_REPORT_OPTION.value) && (
          <div>
            <h5 className="table-title">11th Hour Reporting</h5>
            <EleventhHourReporting
              onExportDataChange={handleEleventhHourExportChange}
            />
          </div>
        )}
      </div>

      <ReportingLogCellModal
        open={cellModalOpen}
        onClose={setCellModalOpen}
        loading={cellModalLoading}
        data={cellModalData}
        tableKey={cellModalTableKey}
        exportContext={exportContext}
      />
    </section>
  );
};

export default ReportingLogs;
